import { JSX } from 'react';
import {
    BooleanInput,
    SelectInput,
    TextInput,
    required,
    regex
} from 'react-admin';

/**
 * The price form, shared by Create and Edit so the two cannot drift apart.
 *
 * Prices are text inputs rather than number inputs on purpose. The column is a Doctrine decimal,
 * which the API exposes as a string, and posting a JSON number to it is refused by the serializer
 * before validation ever runs. Text also keeps a rate like 0.075 exactly as typed rather than
 * letting it travel as a float.
 */

/** The providers `prepare_llm` in the Operator knows how to build. */
const PROVIDER_CHOICES = [
    { id: 'openai', name: 'OpenAI' },
    { id: 'anthropic', name: 'Anthropic' },
    { id: 'mistral', name: 'Mistral' },
    { id: 'ollama', name: 'Ollama (local)' },
    { id: 'ollama-cloud', name: 'Ollama (cloud)' },
];

/**
 * The provider decides how cached input is counted, not just what it is called: OpenAI reports
 * prompt tokens inclusive of the cached share while everyone else reports them separately. Picking
 * the wrong one here does not fail, it quietly bills the cached tokens twice.
 */
const PROVIDER_HELP: string =
    'Sets how cached input is counted. OpenAI includes cached tokens in its input count; the others report them separately.';

const decimalOnly = regex(/^\d+(\.\d{1,6})?$/, 'Use a plain number, up to six decimal places');

export const LlmModelPriceFields: () => JSX.Element = (): JSX.Element => {
    return (
        <>
            <TextInput
                name="modelName"
                source="modelName"
                label="Model selector"
                validate={ [required()] }
                fullWidth
                helperText="Exactly as the Operator records it, for example chatgpt-gpt-5, mistral-medium or ollama-qwen3:32b. This is what a token entry is matched against."
            />
            <TextInput
                name="label"
                source="label"
                label="Display name"
                fullWidth
                helperText="What the dashboard calls it. Falls back to the selector when empty."
            />
            <SelectInput
                name="provider"
                source="provider"
                choices={ PROVIDER_CHOICES }
                validate={ [required()] }
                fullWidth
                helperText={ PROVIDER_HELP }
            />
            <TextInput
                name="inputCostPerMillion"
                source="inputCostPerMillion"
                label="Input, USD per million tokens"
                validate={ [required(), decimalOnly] }
                fullWidth
                helperText="A locally hosted model belongs here at 0, which is a different fact from having no row at all."
            />
            <TextInput
                name="cachedInputCostPerMillion"
                source="cachedInputCostPerMillion"
                label="Cached input, USD per million tokens"
                validate={ [decimalOnly] }
                fullWidth
                helperText="Leave empty when the provider does not price it separately, and cached input is charged at the input rate. Empty is not free."
            />
            <TextInput
                name="outputCostPerMillion"
                source="outputCostPerMillion"
                label="Output, USD per million tokens"
                validate={ [required(), decimalOnly] }
                fullWidth
            />
            <BooleanInput
                name="active"
                source="active"
                defaultValue={ true }
                helperText="An inactive row stops pricing new usage. Its model then reports as unpriced rather than as free."
            />
        </>
    );
}
